import { ILocation } from '@/lib/cachedCms';
import { IButterBaseComponent } from '@lamesarv-sdk/libs/dist/cms/parsers/butter/types/IButterComponents';

export enum PageSchema {
  content = 'content',
  system = 'system',
  brand = 'brand',
  location = 'location',
  landingPage = 'landing-page',
}

export enum CustomComponentType {
  badgeDescription = 'badge_description',
  blogHero = 'blog_hero',
  dealershipHours = 'dealership_hours',
  featureDisplaySection = 'feature_display_section',
  form = 'form',
  frequentlyAskedQuestions = 'frequently_asked_questions',
  genericEventRegistrationForm = 'generic_event_registration_form',
  genericHtml = 'generic_html',
  genericInquiryForm = 'generic_inquiry_form',
  hero = 'hero',
  heroSellAndTrade = 'hero_sell_and_trade',
  heroService = 'hero_service',
  highlightsImage = 'highlights_image',
  homeHero = 'home_hero',
  lifestylePhotoVideoGallerySection = 'lifestyle_photovideo_gallery_section',
  locationImageAndInfo = 'location_image_and_info',
  locations = 'locations',
  locationsList = 'locations_list',
  manufacturerLogos = 'manufacturer_logos',
  processStepsSection = 'process_steps_section',
  productCards = 'product_cards',
  productInfoCards = 'product_info_cards',
  promoCard = 'promo_card',
  promoStripMarquee = 'promo_strip_marquee',
  promotionUnits = 'promotion_units',
  quickInventoryFilter = 'quick_inventory_filter',
  recentArticles = 'recent_articles',
  resourceLinks = 'resource_links',
  reviewCarouselSection = 'review_carousel_section',
  reviewsSection = 'reviews_section',
  rvModelCardsDisplaySection = 'rv_model_cards_display_section',
  rvTypeLinks = 'rv_type_links',
  searchResults = 'search_results',
  simpleBanner = 'simple_banner',
  stayUpdated = 'stay_updated',
  titleAndDescription = 'title_and_description',
  titleDescriptionAndCTA = 'title_description_cta',
  videoSection = 'video_section',
}

export interface IComponentBadgeDescription {
  componentType: CustomComponentType.badgeDescription;
  badge_image_url: string;
  badge_image_alt_text?: string;
  title?: string;
  description?: string;
  classes?: string;
  classes_badge_image?: string;
  classes_title?: string;
  classes_description?: string;
}

export interface IComponentBlogHero {
  componentType: CustomComponentType.blogHero;
  posts: {
    slug: string;
  }[];
}

export interface ICollectionLocation {
  title?: string;
  description?: string;
  state?: string;
  geo_location?: string;
  schedules_timezone?: string;
  sales_phone?: string;
  sales_email?: string;
  sales_state?: string;
  sales_city?: string;
  sales_zipcode?: string;
  sales_address?: string;
  sales_geo_location?: string;
  sales_schedule_weekdays?: string;
  sales_schedule_saturday?: string;
  sales_schedule_sunday?: string;
  service_phone?: string;
  service_email?: string;
  service_state?: string;
  service_city?: string;
  service_zipcode?: string;
  service_address?: string;
  service_geo_location?: string;
  service_schedule_weekdays?: string;
  service_schedule_saturday?: string;
  service_schedule_sunday?: string;
  parts_phone?: string;
  parts_email?: string;
  parts_state?: string;
  parts_city?: string;
  parts_zipcode?: string;
  parts_address?: string;
  parts_geo_location?: string;
  parts_schedule_weekdays?: string;
  parts_schedule_saturday?: string;
  parts_schedule_sunday?: string;
  trade_phone?: string;
  trade_email?: string;
  trade_state?: string;
  trade_city?: string;
  trade_zipcode?: string;
  trade_address?: string;
  trade_geo_location?: string;
  trade_schedule_weekdays?: string;
  trade_schedule_saturday?: string;
  trade_schedule_sunday?: string;
}

export interface IComponentDealershipHours {
  location: ICollectionLocation;
  title?: string;
  classes?: string;
  classes_title?: string;
  classes_dealerships?: string;
  classes_dealership_title?: string;
  custom_sales_title?: string;
  custom_service_title?: string;
  custom_parts_title?: string;
  custom_trade_title?: string;
}

export interface IComponentHomeHero {
  classes?: string;
  componentType: CustomComponentType.homeHero;
  data_tag?: string;
  data_tag_group?: string;
  display_dots?: string;
  display_arrows?: string;
  disable_pause_on_hover?: boolean;
  images: {
    alt: string;
    link_target_blank?: boolean;

    mobile_url: string;
    large_mobile_url: string;
    tablet_url: string;
    small_desktop_url: string;
    medium_desktop_url: string;
    large_desktop_url: string;

    overlay_link: string;

    mobile_overlay_text: string;
    mobile_overlay_subtext: string;
    mobile_overlay_cta_text: string;
    mobile_overlay_cta_link: string;

    tablet_overlay_text: string;
    tablet_overlay_subtext: string;
    tablet_overlay_cta_text: string;
    tablet_overlay_cta_link: string;

    desktop_overlay_text: string;
    desktop_overlay_subtext: string;
    desktop_overlay_cta_text: string;
    desktop_overlay_cta_link: string;

    classes_overlay?: string;
    classes_overlay_text?: string;
    classes_overlay_subtext?: string;
    classes_overlay_cta?: string;
  }[];
  speed?: number;
}

export interface IComponentQuickInventoryFilter {
  componentType: CustomComponentType.quickInventoryFilter;
}

export interface IComponentPromotionUnits {
  componentType: CustomComponentType.promotionUnits;
  type?: string;
  title?: string;
  description?: string;
  cta_text?: string;
  color?: string;
  link: string;
  background_image_url: string;
  filters?: string;
  sort?: string;
  units: {
    stock_number: string;
  }[];
}

export interface IComponentManufacturerLogos {
  componentType: CustomComponentType.manufacturerLogos;
  whitelist?: string;
  disable_links?: boolean;
}

export interface IComponentResourceLinks {
  componentType: CustomComponentType.resourceLinks;
  resources: {
    title: string;
    description: string;
    background_image_url: string;
    link: string;
  }[];
}

export interface IComponentSearchResultsBanner {
  alt?: string;
  link_target_blank?: boolean;
  mobile_image_url?: string;
  mobile_large_image_url?: string;
  tablet_image_url?: string;
  desktop_small_image_url?: string;
  desktop_medium_image_url?: string;
  desktop_large_image_url?: string;
  mobile_overlay_text?: string;
  mobile_overlay_cta_text?: string;
  mobile_overlay_cta_link?: string;
  tablet_overlay_text?: string;
  tablet_overlay_cta_text?: string;
  tablet_overlay_cta_link?: string;
  desktop_overlay_text?: string;
  desktop_overlay_cta_text?: string;
  desktop_overlay_cta_link?: string;
  classes_overlay?: string;
  classes_overlay_text?: string;
  classes_overlay_cta?: string;
}

export interface IComponentSearchResultsFillerCard {
  icon?: string;
  title?: string;
  description?: string;
  cta_text?: string;
  cta_link?: string;
  use_image_instead?: boolean;
  image_url?: string;
  image_action_link?: string;
}

export interface IComponentSearchResults {
  componentType: CustomComponentType.searchResults;
  banner_top_display_dots?: string;
  banner_top_display_arrows?: string;
  banner_top_disable_pause_on_hover?: boolean;
  banner_top_images?: IComponentSearchResultsBanner[];
  banner_top_classes?: string;
  banner_inline_display_dots?: string;
  banner_inline_display_arrows?: string;
  banner_inline_disable_pause_on_hover?: boolean;
  banner_inline_images?: IComponentSearchResultsBanner[];
  banner_inline_classes?: string;
  banner_speed?: number;
  banner_data_tag?: string;
  banner_data_tag_group?: string;
  filler_cards?: IComponentSearchResultsFillerCard[];
}

export interface IComponentSimpleBanner {
  componentType: CustomComponentType.simpleBanner;
  background_image_url: string;
  title: string;
  description: string;
  cta_text: string;
  link: string;
}

export interface IComponentStayUpdated {
  componentType: CustomComponentType.stayUpdated;
  display_sms_checkbox?: boolean;
  custom_sms_text?: string;
}

export interface IComponentRecentArticles {
  componentType: CustomComponentType.recentArticles;
  search?: string;
  cta_style?: string;
  cta_text?: string;
  cta_link?: string;
  cta_classes?: string;
  mobile_amount?: number | string;
  tablet_amount?: number | string;
  desktop_amount?: number | string;
  title?: string;
  title_classes?: string;
  classes?: string;
  post_classes?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentRVTypeLinks {
  componentType: CustomComponentType.rvTypeLinks;
  links: {
    icon: string;
    link: string;
    classes?: string;
  }[];
  classes?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentProductCards {
  componentType: CustomComponentType.productCards;
  classes?: string;
  classes_cta?: string;
  cta_text?: string;
  cta_link?: string;
  mobile_amount: number;
  tablet_amount: number;
  desktop_amount: number;
  filters: string;
  sort?: string;
  desktop_units: {
    stock_number: string;
  }[];
  tablet_units: {
    stock_number: string;
  }[];
  mobile_units: {
    stock_number: string;
  }[];
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentPromoCard {
  componentType: CustomComponentType.promoCard;
  classes?: string;
  classes_background?: string;
  classes_cta?: string;
  classes_description?: string;
  classes_image?: string;
  classes_title?: string;
  description?: string;
  cta_text?: string;
  cta_link?: string;
  image_url: string;
  title?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentReviewsSection {
  componentType: CustomComponentType.reviewsSection;
  title?: string;
  description?: string;
  reviews: {
    author: string;
    state: string;
    review: string;
    display_on_desktop: boolean;
    display_on_tablet: boolean;
    display_on_mobile: boolean;
    classes?: string;
    classes_review?: string;
    classes_author?: string;
    classes_state?: string;
  }[];
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentRVModelCardsDisplaySectionCard {
  image_url: string;
  image_classes?: string;
  chassis_title?: string;
  chassis_title_classes?: string;
  rv_title?: string;
  rv_title_classes?: string;
  cta_text?: string;
  cta_link?: string;
  cta_classes?: string;
  classes?: string;
}

export interface IComponentRVModelCardsDisplaySection {
  componentType: CustomComponentType.rvModelCardsDisplaySection;
  include_1st_section?: boolean;
  '1st_section_title'?: string;
  '1st_section_classes'?: string;
  '1st_section_cards'?: IComponentRVModelCardsDisplaySectionCard[];
  include_2nd_section?: boolean;
  '2nd_section_title'?: string;
  '2nd_section_classes'?: string;
  '2nd_section_cards'?: IComponentRVModelCardsDisplaySectionCard[];
  include_3rd_section?: boolean;
  '3rd_section_title'?: string;
  '3rd_section_classes'?: string;
  '3rd_section_cards'?: IComponentRVModelCardsDisplaySectionCard[];
  classes?: string;
  data_tag?: string;
  data_tag_group?: string;
  alternating_card_background_image?: boolean;
  alternating_card_background_image_url?: string;
}

export interface IComponentFrequentlyAskedQuestions {
  componentType: CustomComponentType.frequentlyAskedQuestions;
  title: string;
  text: string;
  questions: {
    title: string;
    text: string;
    is_open?: boolean;
    icon_url?: string;
    classes_title?: string;
    classes_text?: string;
    classes_container?: string;
    classes_icon?: string;
  }[];
  classes_section?: string;
  classes_container?: string;
  classes?: string;
  classes_title?: string;
  classes_text?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentFeatureDisplaySection {
  componentType: CustomComponentType.featureDisplaySection;
  layout?: string;
  features: {
    title: string;
    icon?: string;
    classes?: string;
    classes_title?: string;
    classes_icon?: string;
  }[];
  data_tag?: string;
  data_tag_group?: string;
  classes?: string;
}

export interface IComponentTitleAndDescription {
  componentType: CustomComponentType.titleAndDescription;
  title: string;
  text: string;
  type: string;
  icons?: {
    icon: string;
    title?: string;
    classes?: string;
    classes_icon?: string;
    classes_title?: string;
  }[];
  classes_container?: string;
  classes?: string;
  classes_title?: string;
  classes_text?: string;
  classes_icons?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentPromoStripMarquee {
  componentType: CustomComponentType.promoStripMarquee;
  title?: string;
  title_url?: string;
  title_target_blank?: boolean;
  alignment: string;
  actions: {
    type: string;
    title: string;
    url: string;
    target_blank: boolean;
    classes: string;
  }[];
  timer_date_time_end: string;
  classes: string;
  classes_title: string;
  classes_actions: string;
  classes_timer: string;
  classes_timer_item: string;
  classes_timer_numbers: string;
  classes_timer_labels: string;
  data_tag: string;
  data_tag_group: string;
}

export interface IComponentVideoSection {
  componentType: CustomComponentType.videoSection;
  video_url: string;
  overlay_text?: string;
  classes?: string;
  classes_overlay?: string;
  classes_overlay_text?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentReviewCarouselSection {
  componentType: CustomComponentType.reviewCarouselSection;
  transition_speed?: string | number;
  time_between_transitions?: string | number;
  reviews: {
    content: string;
    content_classes?: string;
    author: string;
    background_image_url?: string;
    classes?: string;
    classes_stars?: string;
    classes_content?: string;
    classes_author?: string;
    classes_background?: string;
  }[];
  classes?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentProductInfoCards {
  componentType: CustomComponentType.productInfoCards;
  cards: {
    image_url: string;
    title?: string;
    description?: string;
    highlight_icon?: string;
    highlights?: string;
    virtual_tour_title?: string;
    virtual_tour_url?: string;
    virtual_tour_fallback_image_url?: string;
    first_cta_text?: string;
    first_cta_link?: string;
    second_cta_text?: string;
    second_cta_link?: string;
    generic_element?: string;
    classes?: string;
    classes_image?: string;
    classes_title?: string;
    classes_description?: string;
    classes_highlights?: string;
    classes_virtual_tour?: string;
    classes_virtual_tour_title?: string;
    classes_cta?: string;
    classes_generic_element?: string;
  }[];
  classes_container?: string;
  classes?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentLifestylePhotoVideoGallerySection {
  componentType: CustomComponentType.lifestylePhotoVideoGallerySection;
  images: {
    mobile_url: string;
    tablet_url: string;
    desktop_url: string;
    include_overlay_text: boolean;
    overlay_text: string;
    include_overlay_gradient: boolean;
    classes?: string;
    classes_overlay_text?: string;
    classes_overlay_gradient?: string;
  }[];
  data_tag?: string;
  data_tag_group?: string;
  classes?: string;
}

export interface ICollectionLocation {
  title?: string;
  description?: string;
  state?: string;
  geo_location?: string;
  sales_phone?: string;
  sales_email?: string;
  sales_state?: string;
  sales_city?: string;
  sales_zipcode?: string;
  sales_address?: string;
  sales_geo_location?: string;
  sales_place_id?: string;
  sales_schedule_weekdays?: string;
  sales_schedule_saturday?: string;
  sales_schedule_sunday?: string;
  service_phone?: string;
  service_email?: string;
  service_state?: string;
  service_city?: string;
  service_zipcode?: string;
  service_address?: string;
  service_geo_location?: string;
  service_place_id?: string;
  service_schedule_weekdays?: string;
  service_schedule_saturday?: string;
  service_schedule_sunday?: string;
  parts_phone?: string;
  parts_email?: string;
  parts_state?: string;
  parts_city?: string;
  parts_zipcode?: string;
  parts_address?: string;
  parts_geo_location?: string;
  parts_place_id?: string;
  parts_schedule_weekdays?: string;
  parts_schedule_saturday?: string;
  parts_schedule_sunday?: string;
  trade_phone?: string;
  trade_email?: string;
  trade_state?: string;
  trade_city?: string;
  trade_zipcode?: string;
  trade_address?: string;
  trade_geo_location?: string;
  trade_place_id?: string;
  trade_schedule_weekdays?: string;
  trade_schedule_saturday?: string;
  trade_schedule_sunday?: string;
}

export interface IComponentLocationImageAndInfo {
  componentType: CustomComponentType.locationImageAndInfo;
  location: ICollectionLocation;
  title?: string;
  image_url: string;
  inventory_link?: string;
  classes?: string;
  classes_title?: string;
  classes_image?: string;
  classes_addresses?: string;
  classes_inventory_link?: string;
}

export interface IComponentTitleDescriptionAndCTA {
  componentType: CustomComponentType.titleDescriptionAndCTA;
  title: string;
  text: string;
  type: string;
  actions: {
    title: string;
    url: string;
    target_blank: boolean;
    classes: string;
  }[];
  classes?: string;
  classes_title?: string;
  classes_text: string;
  classes_actions?: string;
  data_tag?: string;
  data_tag_group?: string;
}

export interface IComponentGenericFormSubmit {
  formId?: string;
  formType?: string;
  leadType?: string;
  leadSource?: string;
  submit_button_text?: string;
  classes_submit?: string;
}

export interface IComponentGenericFormNext {
  next_button_text?: string;
  classes_next?: string;
}

export interface IComponentGenericFormField {
  type: string;
  required?: boolean;
  custom_text?: string;
  classes?: string;
  classes_container?: string;
  classes_container_label?: string;
  classes_label?: string;
  classes_input?: string;
}

export interface IComponentGenericInquiryForm extends IComponentGenericFormSubmit {
  componentType: CustomComponentType.genericInquiryForm;
  style: string;
  modal?: boolean;
  modal_url_parameter?: string;
  modal_hash_parameter?: string;
  custom_id?: string;
  title?: string;
  description?: string;
  fields: IComponentGenericFormField[];
  products_list?: string;
  display_comments?: boolean;
  display_subscribe_checkbox?: boolean;
  custom_subscribe_text?: string;
  display_sms_checkbox?: boolean;
  custom_sms_text?: string;
  display_terms_checkbox?: boolean;
  custom_terms_text?: string;
  classes_container?: string;
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  message_success?: string;
  message_error?: string;
  data_tag?: string;
  data_tag_group?: string;
  custom_dynamics_id?: string;
  custom_active_campaign_id?: string;
  custom_form_type?: string;
  custom_form_leadtype?: string;
  custom_form_leadsource?: string;
  custom_form_config?: string;
  custom_active_campaign_tag_ids?: string;
  custom_active_campaign_list_ids?: string;
  dynamics_segment?: string;
}

export interface IComponentGenericEventRegistrationForm extends IComponentGenericFormSubmit {
  componentType: CustomComponentType.genericEventRegistrationForm;
  modal?: boolean;
  modal_url_parameter?: string;
  modal_hash_parameter?: string;
  title?: string;
  include_top_border?: boolean;
  classes_top_border?: string;
  description?: string;
  extract_header?: boolean;
  form_type?: string;
  attendance_general?: { text: string }[];
  interests_general?: { text: string }[];
  attendance_influencer?: { text: string }[];
  interests_influencer?: { text: string }[];
  display_subscribe_checkbox?: boolean;
  custom_subscribe_text?: string;
  display_sms_checkbox?: boolean;
  custom_sms_text?: string;
  display_terms_checkbox?: boolean;
  custom_terms_text?: string;
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  classes_attendance?: string;
  classes_interests?: string;
  classes_submit?: string;
  classes_checkboxes?: string;
  message_success?: string;
  message_error?: string;
  data_tag?: string;
  data_tag_group?: string;
  custom_id?: string;
  custom_dynamics_id?: string;
  custom_active_campaign_id?: string;
  custom_form_type?: string;
  custom_form_leadtype?: string;
  custom_form_leadsource?: string;
  custom_form_config?: string;
  custom_active_campaign_tag_ids?: string;
  custom_active_campaign_list_ids?: string;
  dynamics_segment?: string;
}

export interface IComponentGridFavoritesExtraSection {
  title: string;
  description: string;
  view_all_link?: string;
  filters?: string;
}

export interface IComponentSpecificForm extends IComponentGenericFormSubmit, IComponentGenericFormNext {
  componentType: CustomComponentType.form;
  modal?: boolean;
  modal_url_parameter?: string;
  modal_hash_parameter?: string;
  type: string;
  redirect_url?: string;
  custom_id?: string;
  title?: string;
  description?: string;
  extract_header?: boolean;
  classes?: string;
  classes_header?: string;
  classes_title?: string;
  classes_description?: string;
  classes_container?: string;
  classes_content?: string;
  classes_input?: string;
  message_success?: string;
  message_error?: string;
  data_tag?: string;
  data_tag_group?: string;
  display_sms_checkbox?: boolean;
  custom_sms_text?: string;
  custom_form_config?: string;
  dynamics_segment?: string;
}

export interface IComponentHero {
  componentType: CustomComponentType.hero;
  images: {
    image_url: string;
    alt: string;
    image_position?: string;
    image_position_classes?: string;
    overlay_text?: string;
    overlay_classes?: string;
    button_text?: string;
    button_link?: string;
    button_classes?: string;
  }[];
  urls: {
    title: string;
    url: string;
    target_blank?: boolean;
  }[];
  display_arrows?: string;
  display_dots?: string;
  disable_pause_on_hover?: boolean;
  transition_fade?: boolean;
  transition_vertical?: boolean;
  transition_speed?: number;
  classes?: string;
}

export interface IComponentHeroSellAndTrade extends IComponentGenericFormSubmit, IComponentGenericFormNext {
  componentType: CustomComponentType.heroSellAndTrade;
  title?: string;
  description?: string;
  first_panel?: string;
  panel_rv_information_title?: string;
  panel_personal_information_title?: string;
  classes?: string;
  message_success?: string;
  restriction_minimum_year?: number;
  restriction_invalid_rv_types?: string;
  message_success_alternative?: string;
  message_error?: string;
  video_url?: string;
  data_tag?: string;
  data_tag_group?: string;
  classes_title?: string;
  classes_description?: string;
  classes_input?: string;
  classes_container?: string;
  classes_success_title?: string;
  classes_success_text?: string;
  classes_success_button?: string;
  classes_video?: string;
}

export interface IComponentHeroService extends IComponentGenericFormSubmit, IComponentGenericFormNext {
  componentType: CustomComponentType.heroService;
  title?: string;
  description?: string;
  first_panel?: string;
  panel_rv_information_title?: string;
  panel_personal_information_title?: string;
  message_success?: string;
  message_error?: string;
  slider_speed?: number;
  slider_disable_pause_on_hover?: boolean;
  slider_display_arrows?: string;
  slider_display_dots?: string;
  slider_images: {
    alt: string;
    link_target_blank?: boolean;
    mobile_image_url: string;
    mobile_large_image_url: string;
    mobile_overlay_text: string;
    mobile_overlay_subtext: string;
    mobile_overlay_cta_text: string;
    mobile_overlay_cta_link: string;
    tablet_image_url: string;
    tablet_overlay_text: string;
    tablet_overlay_subtext: string;
    tablet_overlay_cta_text: string;
    tablet_overlay_cta_link: string;
    desktop_small_image_url: string;
    desktop_medium_image_url: string;
    desktop_large_image_url: string;
    desktop_overlay_text: string;
    desktop_overlay_subtext: string;
    desktop_overlay_cta_text: string;
    desktop_overlay_cta_link: string;
    classes?: string;
    classes_overlay?: string;
    classes_overlay_text?: string;
    classes_overlay_subtext?: string;
    classes_overlay_cta?: string;
  }[];
  data_tag?: string;
  data_tag_group?: string;
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  classes_input?: string;
  classes_container?: string;
  classes_success_title?: string;
  classes_success_text?: string;
  classes_success_button?: string;
  classes_slider?: string;
}

export interface IComponentLocations {
  title?: string;
  description?: string;
  states: {
    title: string;
    dealerships: string;
    classes?: string;
    classes_title?: string;
    classes_dealerships?: string;
    classes_each_dealership?: string;
  }[];
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  classes_states?: string;
}

export interface IComponentLocationsList {
  title?: string;
  locations_type?: string;
  locations?: {
    location: ILocation;
    url: string;
    target_blank?: boolean;
  }[];
  classes?: string;
  classes_container?: string;
  classes_group_title?: string;
  classes_group_list?: string;
  classes_item_title?: string;
  classes_item_address?: string;
  classes_item_location?: string;
  classes_schedule?: string;
  classes_schedule_status?: string;
  classes_schedule_time?: string;
  classes_sidebar?: string;
  classes_map?: string;
  classes_marker?: string;
  classes_marker_base?: string;
  classes_marker_shadow?: string;
  classes_marker_icon?: string;
  classes_marker_icon_section?: string;
  classes_marker_title?: string;
}

export interface IComponentHighlightsImage {
  title?: string;
  description?: string;
  image_url: string;
  highlights_title?: string;
  classes?: string;
  classes_title?: string;
  classes_description?: string;
  classes_image?: string;
  classes_highlights_title?: string;
  classes_highlights?: string;
  highlights?: {
    text: string;
    icon?: string;
    classes?: string;
    classes_text?: string;
    classes_icon?: string;
  }[];
}

export interface IComponentProcessStepsSection {
  title?: string;
  description?: string;
  steps?: {
    title: string;
    description: string;
    icon?: string;
    classes?: string;
    classes_title?: string;
    classes_description?: string;
    classes_icon?: string;
  }[];
  classes_section?: string;
  classes_container?: string;
  classes?: string;
  classes_title?: string;
  classes_description?: string;
}

const getCustomComponent =
  (componentType: CustomComponentType) =>
  ({ fields }: IButterBaseComponent<unknown>) => ({
    ...fields,
    componentType,
  });

export const customComponentsConverter = {
  ...Object.values(CustomComponentType).reduce(
    (acc, type) => {
      acc[type] = getCustomComponent(type);
      return acc;
    },
    {} as Record<CustomComponentType, (component: IButterBaseComponent<unknown>) => unknown>,
  ),
};

export interface IButterInventorySettings {
  subtitle: string;
  content: string;
  content_html: string;
  filters: string;
  base_columns: number;
  sm_columns: number;
  md_columns: number;
  lg_columns: number;
  xl_columns: number;
  gap: number;
  limit: number | '';
}

export interface IButterLink {
  title: string;
  url: string;
  target_blank: boolean;
}

interface IButterLinkNavBar extends IButterLink {
  children_pages: string;
}

export interface IButterLayout {
  settings_header: {
    logo: string;
    slogan: string;
    search_placeholder: string;
    links_contact: {
      title: string;
      text: string;
      url: string;
    }[];
    links_navbar: IButterLinkNavBar[];
    links_sidebar: IButterLink[];
    links_sidebar_additional: IButterLink[];
  };
  settings_footer: {
    logo: string;
    copyright: string;
    email_offer_title: string;
    links_rvshopping_title: string;
    links_rvresources_title: string;
    links_about_title: string;
    links_social_title: string;
    links_footer: IButterLink[];
    right_links_footer?: IButterLink[];
    links_social: IButterLink[];
    links_about: IButterLink[];
    links_rvresources: IButterLink[];
    links_rvshopping: IButterLink[];
    links_location_california_title: string;
    links_location_california: IButterLink[];
    links_location_arizona_title: string;
    links_location_arizona: IButterLink[];
    links_location_newmexico_title: string;
    links_location_newmexico: IButterLink[];
    links_location_florida_title: string;
    links_location_florida: IButterLink[];
    additional_logos?: {
      image_url: string;
      classes?: string;
    }[];
  };
  settings_search: {
    filters: string;
    base_columns: number;
    sm_columns: number;
    md_columns: number;
    lg_columns: number;
    xl_columns: number;
    gap: number;
    limit: number | '';
    no_price_button_content: string;
  };
  settings_inventory_not_available: IButterInventorySettings;
  settings_inventory_sold: IButterInventorySettings;
  settings_inventory_not_found: IButterInventorySettings;
  settings_inventory: {
    slideouts_icon: string;
    body_style_icon: string;
    fuel_type_icon: string;
    mileage_icon: string;
    sleeps_icon: string;
    length_icon: string;
    engine_icon: string;
    weight_icon: string;
    legal_checkbox_content: string;
    subscribe_checkbox_content: string;
    display_sms_checkbox: boolean;
    custom_sms_text: string;
  };
}
